import { callApi } from "utils/ContentoApi";
import { callComposerAgentApi, callComposerAgentStream } from "services/composerAgent";
import { AgentFilePart, AgentImageRefPart } from "./protocol";

export const CHAT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const EXTENSION_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv"
};

interface UploadUrlResponse {
  signedUrl: string;
  fileKey: string;
}

interface ExtractResponse {
  text: string;
  truncated: boolean;
}

export const chatFileContentType = (file: File): string | null => {
  if (CHAT_IMAGE_TYPES.includes(file.type)) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TYPES[extension] ?? null;
};

export const isSupportedChatFile = (file: File): boolean => chatFileContentType(file) !== null;

const readImageSize = async (file: File): Promise<{ width: number; height: number }> => {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return { width: 0, height: 0 };
  }
};

const uploadChatImage = async (file: File): Promise<AgentImageRefPart> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("timestamp", ((Date.now() / 1000) | 0).toString());
  const uploaded: { path?: string }[] = await callApi({ method: "post", url: "images/upload", data: formData });
  const path = uploaded?.[0]?.path;
  if (!path) throw new Error("The image upload returned no file");
  return { type: "image_ref", url: path, mimeType: file.type, name: file.name, ...(await readImageSize(file)) };
};

const uploadChatDocument = async (accountId: string, file: File, contentType: string): Promise<AgentFilePart> => {
  const { signedUrl, fileKey } = await callComposerAgentApi<UploadUrlResponse>({
    method: "post",
    url: `/accounts/${accountId}/composer-agent/files/upload-url`,
    data: { name: file.name, contentType }
  });
  const response = await fetch(signedUrl, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  const extracted = await callComposerAgentStream<ExtractResponse>(`/accounts/${accountId}/composer-agent/files/extract`, { fileKey, name: file.name });
  return { type: "file", name: file.name, mimeType: contentType, text: extracted.text };
};

export const uploadChatFile = async (accountId: string, file: File): Promise<AgentImageRefPart | AgentFilePart> => {
  const contentType = chatFileContentType(file);
  if (!contentType) throw new Error("Unsupported file type");
  return CHAT_IMAGE_TYPES.includes(contentType) ? uploadChatImage(file) : uploadChatDocument(accountId, file, contentType);
};
